import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container, Row, Col, Card, Button, Spinner, Table,
  Badge, Modal, Form, Tab, Nav, Alert,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import adfService from '../../services/azureDataFactoryService';

const statusBadge = (status) => {
  const map = {
    Succeeded: { bg: 'success', icon: 'check-circle' },
    Failed: { bg: 'danger', icon: 'times-circle' },
    InProgress: { bg: 'primary', icon: 'spinner' },
    Queued: { bg: 'info', icon: 'clock' },
    Canceling: { bg: 'warning', icon: 'ban' },
    Cancelled: { bg: 'secondary', icon: 'ban' },
  };
  const cfg = map[status] || { bg: 'secondary', icon: 'question-circle' };
  return (
    <Badge bg={cfg.bg} className="d-inline-flex align-items-center gap-1">
      <FontAwesomeIcon icon={cfg.icon} spin={status === 'InProgress'} />
      {status}
    </Badge>
  );
};

const formatDuration = (ms) => {
  if (!ms) return '-';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (minutes < 60) return `${minutes}m ${secs}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
};

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleString('es-MX', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const PipelinesTab = ({ pipelineTree, loading, onTrigger }) => {
  const [expandedFolders, setExpandedFolders] = useState({});
  const [search, setSearch] = useState('');

  const toggleFolder = (folder) => {
    setExpandedFolders(prev => ({ ...prev, [folder]: !prev[folder] }));
  };

  const expandAll = () => {
    const all = {};
    (pipelineTree?.folders || []).forEach(f => { all[f.folder] = true; });
    setExpandedFolders(all);
  };

  const collapseAll = () => setExpandedFolders({});

  const filteredFolders = (pipelineTree?.folders || [])
    .map(folder => {
      if (!search.trim()) return folder;
      const q = search.toLowerCase();
      const matchingPipelines = folder.pipelines.filter(
        p => p.name.toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q)
      );
      const folderMatch = folder.folder.toLowerCase().includes(q);
      if (folderMatch) return folder;
      if (matchingPipelines.length > 0) return { ...folder, pipelines: matchingPipelines, count: matchingPipelines.length };
      return null;
    })
    .filter(Boolean);

  const matchCount = filteredFolders.reduce((s, f) => s + f.pipelines.length, 0);

  if (loading) {
    return (
      <Card className="shadow-sm border-0">
        <Card.Body className="text-center py-4">
          <Spinner animation="border" size="sm" variant="primary" />
        </Card.Body>
      </Card>
    );
  }

  if (!pipelineTree?.folders?.length) {
    return (
      <Card className="shadow-sm border-0">
        <Card.Body className="text-center py-4 text-muted">
          <FontAwesomeIcon icon="inbox" size="2x" className="mb-2" />
          <p>No se encontraron pipelines</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm border-0">
      <Card.Header className="bg-white border-bottom py-2">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h6 className="mb-0">
            <FontAwesomeIcon icon="sitemap" className="me-2 text-primary" />
            Pipelines por Carpeta
            <Badge bg="secondary" className="ms-2">{pipelineTree.totalPipelines}</Badge>
            <Badge bg="info" className="ms-1">{pipelineTree.totalFolders} carpetas</Badge>
          </h6>
          <div className="d-flex align-items-center gap-2">
            <div className="position-relative" style={{ width: 240 }}>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Buscar pipeline o carpeta..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  if (e.target.value.trim()) expandAll();
                }}
                style={{ fontSize: '12px', paddingLeft: '30px' }}
              />
              <FontAwesomeIcon
                icon="search"
                className="position-absolute text-muted"
                style={{ left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: '11px' }}
              />
              {search && (
                <FontAwesomeIcon
                  icon="times"
                  className="position-absolute text-muted"
                  style={{ right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: '11px', cursor: 'pointer' }}
                  onClick={() => { setSearch(''); collapseAll(); }}
                />
              )}
            </div>
            <Button variant="falcon-default" size="sm" onClick={expandAll} title="Expandir todo" style={{ fontSize: '11px' }}>
              <FontAwesomeIcon icon="expand" />
            </Button>
            <Button variant="falcon-default" size="sm" onClick={collapseAll} title="Colapsar todo" style={{ fontSize: '11px' }}>
              <FontAwesomeIcon icon="compress" />
            </Button>
          </div>
        </div>
        {search && (
          <small className="text-muted d-block mt-1" style={{ fontSize: '11px' }}>
            {matchCount} pipeline{matchCount !== 1 ? 's' : ''} en {filteredFolders.length} carpeta{filteredFolders.length !== 1 ? 's' : ''}
          </small>
        )}
      </Card.Header>
      <Card.Body className="p-0" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
        {filteredFolders.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <FontAwesomeIcon icon="search" size="2x" className="mb-2 text-300" />
            <p className="mb-0">No se encontraron resultados para "{search}"</p>
          </div>
        ) : (
          filteredFolders.map((folder) => {
            const isExpanded = !!expandedFolders[folder.folder];
            return (
              <div key={folder.folder}>
                <div
                  className="d-flex align-items-center px-3 py-2 border-bottom bg-light"
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleFolder(folder.folder)}
                >
                  <FontAwesomeIcon
                    icon={isExpanded ? 'folder-open' : 'folder'}
                    className="me-2 text-warning"
                  />
                  <span className="fw-semibold flex-grow-1" style={{ fontSize: '13px' }}>
                    {folder.folder}
                  </span>
                  <Badge bg="secondary" className="me-2">{folder.count}</Badge>
                  <FontAwesomeIcon
                    icon={isExpanded ? 'chevron-up' : 'chevron-down'}
                    className="text-muted"
                    style={{ fontSize: '11px' }}
                  />
                </div>
                {isExpanded && (
                  <Table hover className="mb-0" style={{ fontSize: '12px' }}>
                    <tbody>
                      {folder.pipelines.map((p) => (
                        <tr key={p.name}>
                          <td className="ps-4" style={{ width: '35%' }}>
                            <FontAwesomeIcon icon="code-branch" className="me-2 text-primary" style={{ fontSize: '11px' }} />
                            <span className="fw-semibold">{p.name}</span>
                          </td>
                          <td className="text-muted text-truncate" style={{ maxWidth: 180 }}>
                            {p.description || '-'}
                          </td>
                          <td className="text-center" style={{ width: '80px' }}>
                            <Badge bg="info" style={{ fontSize: '10px' }}>{p.activities} act.</Badge>
                          </td>
                          <td style={{ width: '25%' }}>
                            {p.parameters?.length > 0 ? (
                              p.parameters.slice(0, 3).map((param) => (
                                <Badge bg="light" text="dark" className="me-1 border" key={param} style={{ fontSize: '10px' }}>
                                  {param}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                            {p.parameters?.length > 3 && (
                              <Badge bg="light" text="muted" className="border" style={{ fontSize: '10px' }}>
                                +{p.parameters.length - 3}
                              </Badge>
                            )}
                          </td>
                          <td className="text-center" style={{ width: '90px' }}>
                            <Button variant="falcon-success" size="sm" onClick={() => onTrigger(p)} style={{ fontSize: '11px' }}>
                              <FontAwesomeIcon icon="play" className="me-1" />
                              Ejecutar
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            );
          })
        )}
      </Card.Body>
    </Card>
  );
};

const RunsTab = ({ runs, loading, onViewDetails, onCancel, onRefresh }) => (
  <Card className="shadow-sm border-0">
    <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
      <h6 className="mb-0">
        <FontAwesomeIcon icon="history" className="me-2 text-info" />
        Ejecuciones Recientes
        <Badge bg="secondary" className="ms-2">{runs.length}</Badge>
      </h6>
      <Button variant="falcon-primary" size="sm" onClick={onRefresh}>
        <FontAwesomeIcon icon="sync" className="me-1" />
        Actualizar
      </Button>
    </Card.Header>
    <Card.Body className="p-0">
      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" variant="primary" />
        </div>
      ) : runs.length === 0 ? (
        <div className="text-center py-4 text-muted">
          <FontAwesomeIcon icon="inbox" size="2x" className="mb-2 text-300" />
          <p>No hay ejecuciones recientes</p>
        </div>
      ) : (
        <div className="table-responsive">
          <Table hover className="mb-0" style={{ fontSize: '13px' }}>
            <thead className="bg-light">
              <tr>
                <th>Pipeline</th>
                <th>Estado</th>
                <th>Inicio</th>
                <th>Fin</th>
                <th>Duración</th>
                <th>Iniciado por</th>
                <th className="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr key={run.runId}>
                  <td className="fw-semibold">{run.pipelineName}</td>
                  <td>{statusBadge(run.status)}</td>
                  <td className="text-muted">{formatDate(run.runStart)}</td>
                  <td className="text-muted">{formatDate(run.runEnd)}</td>
                  <td>{formatDuration(run.durationMs)}</td>
                  <td className="text-muted">{run.invokedBy?.name || '-'}</td>
                  <td className="text-center">
                    <div className="d-flex gap-1 justify-content-center">
                      <Button
                        variant="falcon-info"
                        size="sm"
                        onClick={() => onViewDetails(run)}
                        title="Ver detalle"
                      >
                        <FontAwesomeIcon icon="search" />
                      </Button>
                      {run.status === 'InProgress' && (
                        <Button
                          variant="falcon-danger"
                          size="sm"
                          onClick={() => onCancel(run.runId)}
                          title="Cancelar"
                        >
                          <FontAwesomeIcon icon="stop" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Card.Body>
  </Card>
);

const TriggerModal = ({ show, onHide, pipeline, onConfirm, triggering }) => {
  const [params, setParams] = useState({});

  useEffect(() => {
    if (pipeline?.parameters) {
      const init = {};
      pipeline.parameters.forEach((p) => { init[p] = ''; });
      setParams(init);
    }
  }, [pipeline]);

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fs-6">
          <FontAwesomeIcon icon="play-circle" className="me-2 text-success" />
          Ejecutar Pipeline
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">
          Pipeline: <strong>{pipeline?.name}</strong>
        </p>
        {pipeline?.parameters?.length > 0 && (
          <>
            <p className="text-muted fs-11 mb-2">Parámetros (opcionales):</p>
            {pipeline.parameters.map((param) => (
              <Form.Group className="mb-2" key={param}>
                <Form.Label className="fs-11 fw-semibold">{param}</Form.Label>
                <Form.Control
                  size="sm"
                  value={params[param] || ''}
                  onChange={(e) => setParams({ ...params, [param]: e.target.value })}
                  placeholder={`Valor para ${param}`}
                />
              </Form.Group>
            ))}
          </>
        )}
        <Alert variant="warning" className="mb-0 mt-3 fs-11">
          <FontAwesomeIcon icon="exclamation-triangle" className="me-1" />
          Esta acción ejecutará el pipeline en Azure Data Factory. El proceso puede tardar varios minutos.
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide}>
          Cancelar
        </Button>
        <Button variant="success" size="sm" onClick={() => onConfirm(params)} disabled={triggering}>
          {triggering ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              Ejecutando...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon="play" className="me-1" />
              Confirmar Ejecución
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const DetailsModal = ({ show, onHide, run, activities, loadingActivities }) => (
  <Modal show={show} onHide={onHide} size="lg" centered scrollable>
    <Modal.Header closeButton className="bg-light">
      <Modal.Title className="fs-6">
        <FontAwesomeIcon icon="info-circle" className="me-2 text-info" />
        Detalle de Ejecución
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {run && (
        <>
          <Row className="g-3 mb-3">
            <Col md={6}>
              <small className="text-muted d-block">Pipeline</small>
              <span className="fw-bold">{run.pipelineName}</span>
            </Col>
            <Col md={6}>
              <small className="text-muted d-block">Estado</small>
              {statusBadge(run.status)}
            </Col>
            <Col md={6}>
              <small className="text-muted d-block">Inicio</small>
              <span>{formatDate(run.runStart)}</span>
            </Col>
            <Col md={6}>
              <small className="text-muted d-block">Fin</small>
              <span>{formatDate(run.runEnd)}</span>
            </Col>
            <Col md={6}>
              <small className="text-muted d-block">Duración</small>
              <span>{formatDuration(run.durationMs)}</span>
            </Col>
            <Col md={6}>
              <small className="text-muted d-block">Run ID</small>
              <code className="fs-10">{run.runId}</code>
            </Col>
            {run.message && (
              <Col md={12}>
                <small className="text-muted d-block">Mensaje</small>
                <Alert variant={run.status === 'Failed' ? 'danger' : 'info'} className="mb-0 fs-11 py-1 px-2">
                  {run.message}
                </Alert>
              </Col>
            )}
          </Row>

          <h6 className="mb-2 mt-3">
            <FontAwesomeIcon icon="tasks" className="me-2 text-primary" />
            Actividades
          </h6>
          {loadingActivities ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" />
            </div>
          ) : activities.length === 0 ? (
            <p className="text-muted fs-11">No se encontraron actividades</p>
          ) : (
            <Table size="sm" hover className="mb-0" style={{ fontSize: '12px' }}>
              <thead className="bg-light">
                <tr>
                  <th>Actividad</th>
                  <th>Tipo</th>
                  <th>Estado</th>
                  <th>Duración</th>
                </tr>
              </thead>
              <tbody>
                {activities.map((a, i) => (
                  <tr key={i}>
                    <td className="fw-semibold">{a.activityName}</td>
                    <td className="text-muted">{a.activityType}</td>
                    <td>{statusBadge(a.status)}</td>
                    <td>{formatDuration(a.durationMs)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" size="sm" onClick={onHide}>Cerrar</Button>
    </Modal.Footer>
  </Modal>
);

const DataFactoryDashboard = () => {
  const [activeTab, setActiveTab] = useState('pipelines');
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [pipelines, setPipelines] = useState([]);
  const [pipelineTree, setPipelineTree] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loadingPipelines, setLoadingPipelines] = useState(true);
  const [loadingRuns, setLoadingRuns] = useState(true);

  const [showTriggerModal, setShowTriggerModal] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [triggering, setTriggering] = useState(false);

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);

  const pollingRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const status = await adfService.getStatus();
      setConnectionStatus(status);
    } catch {
      setConnectionStatus({ configured: false });
    }
  }, []);

  const loadPipelines = useCallback(async () => {
    setLoadingPipelines(true);
    try {
      const [listData, treeData] = await Promise.all([
        adfService.listPipelines(),
        adfService.getPipelineTree(),
      ]);
      setPipelines(Array.isArray(listData) ? listData : listData?.data || []);
      setPipelineTree(treeData);
    } catch (err) {
      console.error('Error loading pipelines:', err);
      toast.error('Error al cargar pipelines');
    } finally {
      setLoadingPipelines(false);
    }
  }, []);

  const loadRuns = useCallback(async () => {
    setLoadingRuns(true);
    try {
      const data = await adfService.getPipelineRuns({});
      const runsArr = Array.isArray(data) ? data : data?.data || [];
      setRuns(runsArr);

      const hasInProgress = runsArr.some((r) => r.status === 'InProgress');
      if (hasInProgress && !pollingRef.current) {
        pollingRef.current = setInterval(async () => {
          try {
            const refreshed = await adfService.getPipelineRuns({});
            const arr = Array.isArray(refreshed) ? refreshed : refreshed?.data || [];
            setRuns(arr);
            if (!arr.some((r) => r.status === 'InProgress')) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
              toast.success('Pipeline finalizado');
            }
          } catch { /* ignore polling errors */ }
        }, 30000);
      }
    } catch (err) {
      console.error('Error loading runs:', err);
      toast.error('Error al cargar ejecuciones');
    } finally {
      setLoadingRuns(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadPipelines();
    loadRuns();
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [loadStatus, loadPipelines, loadRuns]);

  const handleTrigger = (pipeline) => {
    setSelectedPipeline(pipeline);
    setShowTriggerModal(true);
  };

  const handleConfirmTrigger = async (params) => {
    setTriggering(true);
    try {
      const cleanParams = {};
      Object.entries(params || {}).forEach(([k, v]) => {
        if (v !== '') cleanParams[k] = v;
      });
      const result = await adfService.triggerPipeline(
        selectedPipeline.name,
        Object.keys(cleanParams).length > 0 ? cleanParams : undefined,
      );
      toast.success(`Pipeline '${selectedPipeline.name}' ejecutado. Run ID: ${result?.runId || 'N/A'}`);
      setShowTriggerModal(false);
      setActiveTab('runs');
      setTimeout(() => loadRuns(), 2000);
    } catch (err) {
      toast.error(`Error al ejecutar pipeline: ${err.message}`);
    } finally {
      setTriggering(false);
    }
  };

  const handleViewDetails = async (run) => {
    setSelectedRun(run);
    setShowDetailsModal(true);
    setLoadingActivities(true);
    try {
      const acts = await adfService.getRunActivities(run.runId);
      setActivities(Array.isArray(acts) ? acts : acts?.data || []);
    } catch {
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleCancel = async (runId) => {
    if (!window.confirm('¿Está seguro de cancelar esta ejecución?')) return;
    try {
      await adfService.cancelRun(runId);
      toast.success('Ejecución cancelada');
      loadRuns();
    } catch (err) {
      toast.error(`Error al cancelar: ${err.message}`);
    }
  };

  const succeededCount = runs.filter((r) => r.status === 'Succeeded').length;
  const failedCount = runs.filter((r) => r.status === 'Failed').length;
  const inProgressCount = runs.filter((r) => r.status === 'InProgress').length;

  return (
    <Container fluid className="px-3">
      {/* Header */}
      <div className="mb-4">
        <Row className="align-items-center">
          <Col>
            <h2 className="mb-1">
              <FontAwesomeIcon icon="database" className="me-2 text-primary" />
              Azure Data Factory
            </h2>
            <p className="text-muted mb-0 fs-11">
              Gestión de pipelines ETL · {connectionStatus?.factoryName || 'Cargando...'}
              {connectionStatus?.configured ? (
                <Badge bg="success" className="ms-2 fs-10">Conectado</Badge>
              ) : (
                <Badge bg="warning" className="ms-2 fs-10">No configurado</Badge>
              )}
            </p>
          </Col>
        </Row>
      </div>

      {!connectionStatus?.configured && connectionStatus !== null && (
        <Alert variant="warning" className="mb-4">
          <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
          <strong>Azure Data Factory no configurado.</strong> Agregue las variables de entorno
          AZURE_TENANT_ID, AZURE_CLIENT_ID, AZURE_CLIENT_SECRET, AZURE_SUBSCRIPTION_ID,
          AZURE_RESOURCE_GROUP y AZURE_DATA_FACTORY_NAME en el archivo .env del backend.
        </Alert>
      )}

      {/* KPIs rápidos */}
      <Row className="g-3 mb-4">
        <Col md={3}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body className="py-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-uppercase text-500 fw-bold fs-10">Pipelines</small>
                  <h3 className="mb-0">{pipelines.length}</h3>
                </div>
                <div className="rounded-circle bg-primary-subtle d-flex align-items-center justify-content-center"
                  style={{ width: 45, height: 45 }}>
                  <FontAwesomeIcon icon="project-diagram" className="text-primary" />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body className="py-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-uppercase text-500 fw-bold fs-10">Exitosos</small>
                  <h3 className="mb-0 text-success">{succeededCount}</h3>
                </div>
                <div className="rounded-circle bg-success-subtle d-flex align-items-center justify-content-center"
                  style={{ width: 45, height: 45 }}>
                  <FontAwesomeIcon icon="check-circle" className="text-success" />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body className="py-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-uppercase text-500 fw-bold fs-10">Fallidos</small>
                  <h3 className="mb-0 text-danger">{failedCount}</h3>
                </div>
                <div className="rounded-circle bg-danger-subtle d-flex align-items-center justify-content-center"
                  style={{ width: 45, height: 45 }}>
                  <FontAwesomeIcon icon="times-circle" className="text-danger" />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body className="py-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-uppercase text-500 fw-bold fs-10">En Progreso</small>
                  <h3 className="mb-0 text-primary">{inProgressCount}</h3>
                </div>
                <div className="rounded-circle bg-info-subtle d-flex align-items-center justify-content-center"
                  style={{ width: 45, height: 45 }}>
                  <FontAwesomeIcon icon="spinner" className="text-primary" spin={inProgressCount > 0} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabs */}
      <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
        <Card className="shadow-sm border-0 mb-3">
          <Card.Body className="py-0">
            <Nav variant="pills" className="py-2">
              <Nav.Item>
                <Nav.Link
                  eventKey="pipelines"
                  className={`px-4 py-2 me-2 rounded-pill ${activeTab === 'pipelines' ? 'bg-primary text-white' : 'bg-light text-dark'}`}
                  style={{ cursor: 'pointer' }}
                >
                  <FontAwesomeIcon icon="project-diagram" className="me-1" />
                  Pipelines
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link
                  eventKey="runs"
                  className={`px-4 py-2 rounded-pill ${activeTab === 'runs' ? 'bg-primary text-white' : 'bg-light text-dark'}`}
                  style={{ cursor: 'pointer' }}
                >
                  <FontAwesomeIcon icon="history" className="me-1" />
                  Ejecuciones
                  {inProgressCount > 0 && (
                    <Badge bg="warning" className="ms-1">{inProgressCount}</Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Card.Body>
        </Card>

        <Tab.Content>
          <Tab.Pane eventKey="pipelines">
            <PipelinesTab
              pipelineTree={pipelineTree}
              loading={loadingPipelines}
              onTrigger={handleTrigger}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="runs">
            <RunsTab
              runs={runs}
              loading={loadingRuns}
              onViewDetails={handleViewDetails}
              onCancel={handleCancel}
              onRefresh={loadRuns}
            />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      <TriggerModal
        show={showTriggerModal}
        onHide={() => setShowTriggerModal(false)}
        pipeline={selectedPipeline}
        onConfirm={handleConfirmTrigger}
        triggering={triggering}
      />

      <DetailsModal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        run={selectedRun}
        activities={activities}
        loadingActivities={loadingActivities}
      />
    </Container>
  );
};

export default DataFactoryDashboard;
